/**
 * Normalises a picked or dropped image file into something every browser can
 * decode. iPhone photos arrive as HEIC/HEIF, which Chrome, Edge and Firefox
 * cannot draw onto a canvas (only Safari can), so they are converted to JPEG
 * first with heic-to (libheif compiled to WebAssembly). The library is loaded
 * on demand, only when a HEIC file is actually picked, so it costs nothing for
 * ordinary JPG/PNG uploads.
 */

const HEIC_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);
const HEIC_EXTENSION = /\.(heic|heif)$/i;

export const isHeicFile = (file: File): boolean =>
  HEIC_TYPES.has(file.type.toLowerCase()) || HEIC_EXTENSION.test(file.name);

/**
 * Returns the file itself for normal images, or a JPEG File for HEIC/HEIF.
 * Throws if a HEIC file cannot be converted.
 */
export const toBrowserImage = async (file: File): Promise<File> => {
  if (!isHeicFile(file)) return file;

  const { heicTo } = await import('heic-to');
  const blob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 });
  const name = file.name.replace(HEIC_EXTENSION, '') + '.jpg';
  return new File([blob], name, { type: 'image/jpeg', lastModified: file.lastModified });
};
